import { useEffect, useRef, useState, type ChangeEvent } from 'react';
import Checkbox from 'threads5/Checkbox';
import styled from 'threads5/styles/styled';
import ThemeProvider from 'threads5/themes/theme-provider';
import { useComponentTheme } from 'src/hooks/useComponentTheme';

export const USER_INTENT_MARKETO_ID = '2142';

export const isUserIntentForm = (marketoId?: string | number | null) => {
  return String(marketoId ?? '') === USER_INTENT_MARKETO_ID;
};

// Shown only on Marketo form 2142. `id` is the approved form's field Name,
// which is the key MktoForms2.vals accepts. Checked values are "yes" and
// unchecked values are "no", matching those checkbox inputs. Every field is
// written on mount so it is present in the submit payload even when the box
// stays unchecked.
// Row-major order so a two-column grid matches the design: left column
// Lending, Fraud prevention, Money movement; right column Cash advance,
// Financial insights, Onboarding.
export const USER_INTENT_OPTIONS = [
  { id: 'fR26Lending', label: 'Lending' },
  { id: 'fR26CashAdvance', label: 'Cash advance' },
  { id: 'fR26FraudPrevention', label: 'Fraud prevention' },
  { id: 'fR26FinancialInsights', label: 'Financial insights' },
  { id: 'fR26MoneyMovement', label: 'Money movement' },
  { id: 'fR26Onboarding', label: 'Onboarding' },
];

const IntentFields = styled('fieldset')({
  border: 0,
  margin: '0 0 20px',
  minWidth: 0,
  padding: 0,
  width: '100%',
});

const IntentLegend = styled('legend')(({ theme }) => {
  return {
    color: theme.palette['base/black/1000'],
    fontFamily: theme.typography.plaidSans,
    fontSize: '1.6rem',
    fontWeight: 500,
    lineHeight: '133%',
    marginBottom: '16px',
    padding: 0,
  };
});

const checkboxBox = '& .Thr-Checkbox > span:not(.MuiTouchRipple-root)';

const IntentOptions = styled('div')(({ theme }) => {
  return {
    columnGap: theme.spacing(4),
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
    rowGap: theme.spacing(1),
    [checkboxBox]: {
      backgroundColor: theme.palette['base/white'],
      border: `1px solid ${theme.palette['base/sky/900']}`,
      borderRadius: '2px',
      boxSizing: 'border-box',
    },
    [`& .Thr-Checkbox.Mui-checked > span:not(.MuiTouchRipple-root)`]: {
      backgroundColor: theme.palette['base/black/1000'],
      borderColor: theme.palette['base/black/1000'],
    },
    '& .Thr-Checkbox.Mui-checked svg': {
      color: theme.palette['base/white'],
    },
    '& label': {
      color: theme.palette['base/black/1000'],
      cursor: 'pointer',
      fontFamily: theme.typography.plaidSans,
      fontSize: '1.6rem',
      fontWeight: 400,
      lineHeight: '24px',
      marginLeft: '8px',
      whiteSpace: 'nowrap',
    },
  };
});

const UserIntentFields = ({
  setValue,
}: {
  setValue: (name: string, value: string) => void;
}) => {
  const lightTheme = useComponentTheme('light');
  const [selected, setSelected] = useState<Record<string, boolean>>({});
  const selectedRef = useRef(selected);
  selectedRef.current = selected;

  useEffect(() => {
    USER_INTENT_OPTIONS.forEach((option) => {
      setValue(option.id, selectedRef.current[option.id] ? 'yes' : 'no');
    });
  }, [setValue]);

  return (
    <ThemeProvider theme={lightTheme}>
      <IntentFields className='cms-contact-form-user-intent'>
        <IntentLegend>What are you interested in?</IntentLegend>
        <IntentOptions>
          {USER_INTENT_OPTIONS.map((option) => {
            return (
              <Checkbox
                key={option.id}
                checked={selected[option.id] === true}
                id={`user-intent-${option.id}`}
                label={option.label}
                name={option.id}
                size='20'
                value='yes'
                onChange={(
                  _event: ChangeEvent<HTMLInputElement>,
                  checked: boolean,
                ) => {
                  setSelected((current) => {
                    return { ...current, [option.id]: checked };
                  });
                  setValue(option.id, checked ? 'yes' : 'no');
                }}
              />
            );
          })}
        </IntentOptions>
      </IntentFields>
    </ThemeProvider>
  );
};

export default UserIntentFields;
